import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ClientApi } from '../../../apis';
import { toast } from 'react-toastify';

// Validation schema using Yup
const validationSchema = Yup.object({
  clientNickname: Yup.string().required('Client nickname is required'),
  company: Yup.string().required('Company is required'),
  contactNumber: Yup.string().required('Contact number is required'),
  phone: Yup.string()
    .matches(/^[\d\s\-\+\(\)]+$/, 'Invalid phone number format')
    .min(10, 'Phone number must be at least 10 digits')
    .required('Phone is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  billingAddress: Yup.string().required('Billing address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, 'Invalid zip code format')
    .required('Zip code is required'),
  accountsPayableEmail: Yup.string()
    .email('Invalid email address')
    .required('Accounts payable email is required'),
  notes: Yup.string(),
});

function AddClient() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      clientNickname: '',
      company: '',
      contactNumber: '',
      phone: '',
      email: '',
      billingAddress: '',
      city: '',
      state: '',
      zipCode: '',
      accountsPayableEmail: '',
      notes: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await ClientApi.createClient(values);
        
        if (response.status === 200 || response.status === 201) {
          toast.success('Client added successfully!');
          navigate('/admin/clients');
        }
      } catch (error) {
        console.error('Error creating client:', error);
        toast.error(error.response?.data?.message || 'Failed to create client');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom mb={4}>
        Add Client
      </Typography>

      <Paper elevation={3} sx={{ padding: 4 }}>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            '& .MuiTextField-root': { mb: 2 },
          }}
        >
          <Grid container spacing={3}>
            {/* Client Nickname */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Client Nickname *
              </Typography>
              <TextField
                fullWidth
                id="clientNickname"
                name="clientNickname"
                placeholder="Enter client nickname"
                value={formik.values.clientNickname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.clientNickname && Boolean(formik.errors.clientNickname)}
                helperText={formik.touched.clientNickname && formik.errors.clientNickname}
              />
            </Grid>

            {/* Company */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Company *
              </Typography>
              <TextField
                fullWidth
                id="company"
                name="company"
                placeholder="Enter company name"
                value={formik.values.company}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
              />
            </Grid>

            {/* Contact Number */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Contact Number *
              </Typography>
              <TextField
                fullWidth
                id="contactNumber"
                name="contactNumber"
                placeholder="Enter contact number"
                value={formik.values.contactNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.contactNumber && Boolean(formik.errors.contactNumber)}
                helperText={formik.touched.contactNumber && formik.errors.contactNumber}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Phone *
              </Typography>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                placeholder="Enter phone number"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Email *
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>

            {/* Accounts Payable Email */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Accounts Payable Email *
              </Typography>
              <TextField
                fullWidth
                id="accountsPayableEmail"
                name="accountsPayableEmail"
                type="email"
                placeholder="Enter accounts payable email"
                value={formik.values.accountsPayableEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountsPayableEmail && Boolean(formik.errors.accountsPayableEmail)}
                helperText={formik.touched.accountsPayableEmail && formik.errors.accountsPayableEmail}
              />
            </Grid>

            {/* Billing Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Billing Address *
              </Typography>
              <TextField
                fullWidth
                id="billingAddress"
                name="billingAddress"
                placeholder="Enter billing address"
                value={formik.values.billingAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.billingAddress && Boolean(formik.errors.billingAddress)}
                helperText={formik.touched.billingAddress && formik.errors.billingAddress}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                City *
              </Typography>
              <TextField
                fullWidth
                id="city"
                name="city"
                placeholder="Enter city"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                State *
              </Typography>
              <TextField
                fullWidth
                id="state"
                name="state"
                placeholder="Enter state"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
              />
            </Grid>

            {/* Zip Code */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Zip Code *
              </Typography>
              <TextField
                fullWidth
                id="zipCode"
                name="zipCode"
                placeholder="Enter zip code"
                value={formik.values.zipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
                helperText={formik.touched.zipCode && formik.errors.zipCode}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" component="div" gutterBottom fontWeight={500}>
                Notes
              </Typography>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                multiline
                rows={4}
                placeholder="Enter any additional notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                        variant="contained"
                        color="primary"
                  onClick={() => navigate('/admin/clients')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default AddClient;

